import React from 'react';
import { Card, Input, Switch, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * "Create an account" — offered WHILE the customer is filling in their details to
 * approve, not after the approval has already happened.
 *
 * Owner ask 2026-07-30: *"move create account to when approving estimate not after
 * approval while customer is already typing in information"*. The account offer used
 * to appear only once `status === 'approved'`, which meant the customer typed their
 * name, phone and address to approve, pressed Approve, and was then asked to fill a
 * second form with the same fields plus a password.
 *
 * This holds only the part that is genuinely new — the password. Everything else it
 * would need (name, email, phone) is already being collected a few rows above by
 * `MissingInfoPrompt`, so asking again here would recreate the duplication this
 * change removes. `PublicEstimateView` sends both together: the approval first, then
 * the registration with the same portal link that authorized the approval.
 *
 * Off by default. An account is useful, not a condition of approving, so nothing
 * here can block the approve button unless the customer opted in and then left the
 * password unusable.
 */

/** Server policy is length-only (backend/src/utils/password.ts). */
export const MIN_PASSWORD_LENGTH = 8;

export interface ApprovalAccountSetupProps {
  /** Whether the customer wants an account. Controlled by the parent. */
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  password: string;
  onPasswordChange: (password: string) => void;
  confirm: string;
  onConfirmChange: (confirm: string) => void;
  /**
   * The identifier the account will be keyed on — whatever the customer has typed
   * or the shop already holds. Shown so they know which one they will sign in with,
   * and absent means there is nothing to make an account from.
   */
  identifier?: string | null;
  /** Set after a failed attempt so the customer sees which box to fix. */
  error?: string | null;
  /** True once the account exists; the offer collapses to a confirmation. */
  created?: boolean;
  disabled?: boolean;
}

/**
 * Why an opted-in setup cannot be submitted. `null` means "good to go".
 * Exported because the approve handler must apply the SAME rule before sending —
 * a component that only reports problems visually is a component the submit path
 * can forget to consult.
 */
export function accountSetupProblem(args: {
  enabled: boolean;
  password: string;
  confirm: string;
  identifier?: string | null;
}): string | null {
  if (!args.enabled) return null;
  if (!args.identifier) {
    return 'We need an email address or phone number above before we can make an account.';
  }
  if (args.password.length < MIN_PASSWORD_LENGTH) {
    return `Please choose a password of at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  if (args.password !== args.confirm) return 'The two passwords do not match.';
  return null;
}

export function ApprovalAccountSetup({
  enabled,
  onEnabledChange,
  password,
  onPasswordChange,
  confirm,
  onConfirmChange,
  identifier,
  error,
  created,
  disabled,
}: ApprovalAccountSetupProps): React.ReactElement {
  if (created) {
    return (
      <Card size="small" style={{ marginTop: 12 }} data-testid="approval-account-setup">
        <Text strong>Your account is ready.</Text>{' '}
        <Text type="secondary">
          Sign in with {identifier || 'the details above'} and the password you chose.
        </Text>
      </Card>
    );
  }

  return (
    <Card size="small" style={{ marginTop: 12 }} data-testid="approval-account-setup">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Switch
          checked={enabled}
          onChange={onEnabledChange}
          disabled={disabled}
          data-testid="account-optin"
          aria-label="Create an account"
        />
        <div>
          <Text strong style={{ fontSize: 14 }}>
            Create an account while you are here
          </Text>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Keeps your history, invoices and receipts in one place, and makes booking
              next time faster. Optional — approving works either way.
            </Text>
          </div>
        </div>
      </div>

      {enabled && (
        <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {identifier
                ? `You will sign in with ${identifier}.`
                : 'Add an email address or phone number above and we will use it to sign you in.'}
            </Text>
          </div>
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={`Password — at least ${MIN_PASSWORD_LENGTH} characters`}
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            disabled={disabled}
            data-testid="account-password"
            autoComplete="new-password"
            // 16px stops mobile Safari zooming the viewport on focus.
            style={{ fontSize: 16 }}
          />
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Repeat the password"
            value={confirm}
            onChange={(e) => onConfirmChange(e.target.value)}
            disabled={disabled}
            data-testid="account-password-confirm"
            autoComplete="new-password"
            style={{ fontSize: 16 }}
          />
          {error && (
            <Text type="danger" style={{ fontSize: 12 }} data-testid="account-error">
              {error}
            </Text>
          )}
        </div>
      )}
    </Card>
  );
}

export default ApprovalAccountSetup;
